import { forwardRef, MouseEvent, useImperativeHandle, useRef, useState } from 'react';
import cn from 'classnames';

import { useIwBreakPoints } from '@/_pages/interwill/hooks/useIwBreakPoints';
import { DocxDocument } from '@/shared/components/DocxDocument';

import { DocxThumbnail, DocxWrapper, ImageWrapper, Sidebar } from '../styled';
import { Container, StyledText } from '../ThumbnailItem/styled';
import { FilePreviewData } from '../types';

type DocxPreviewProps = {
  fileData: FilePreviewData;
};
const THUMB_TARGET_WIDTH = 200;

export const DocxPreview = forwardRef<HTMLDivElement, DocxPreviewProps>(({ fileData }, ref) => {
  const [preview, setPreview] = useState<HTMLDivElement[]>([]);
  const [activePage, setActivePage] = useState(0);

  const pageRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => pageRef.current as HTMLDivElement);

  const { isMobile } = useIwBreakPoints();

  const onRendered = () => {
    if (!pageRef.current) return;

    const pages = Array.from(pageRef.current.querySelectorAll<HTMLDivElement>('.docx'));

    const arr: HTMLDivElement[] = [];
    pages.forEach((pageEl, i) => {
      const el = document.createElement('div');
      const clone = pageEl.cloneNode(true) as HTMLDivElement;
      const aspectRatio = pageEl.clientWidth / pageEl.clientHeight;

      pageEl.dataset.page = String(i);
      clone.style.transformOrigin = 'top left';
      clone.style.transform = `scale(${THUMB_TARGET_WIDTH / pageEl.clientWidth})`;
      clone.style.margin = '0';
      clone.style.pointerEvents = 'none';
      clone.style.display = 'block';

      el.style.width = `${THUMB_TARGET_WIDTH}px`;
      el.style.height = `${THUMB_TARGET_WIDTH / aspectRatio}px`;
      el.appendChild(clone);
      arr.push(el);
    });
    setPreview(arr);
  };

  const scrollDocument = (pageNumber: number) => {
    if (!pageRef.current) return;

    const pages = Array.from(pageRef.current.querySelectorAll<HTMLDivElement>('.docx'));

    pages[pageNumber]?.scrollIntoView({ behavior: 'smooth' });
  };

  const onMouseOver = (e: MouseEvent) => {
    if (!sidebarRef.current) return;

    const pageEl: HTMLElement | null = (e.target as HTMLElement)?.closest('.docx');
    const page = Number(pageEl?.dataset?.page);
    if (!Number.isNaN(page)) {
      setActivePage(page);
      sidebarRef.current.children[page]?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <ImageWrapper>
      {!isMobile && (
        <Sidebar ref={sidebarRef} className="rendered">
          {preview.map((item, i) => (
            <Container
              className={cn({ active: i === activePage })}
              key={i}
              onClick={() => {
                setActivePage(i);
                scrollDocument(i);
              }}
            >
              <DocxThumbnail ref={(docRef) => docRef?.appendChild(item)}></DocxThumbnail>
              <StyledText>{i + 1}</StyledText>
            </Container>
          ))}
        </Sidebar>
      )}
      <DocxWrapper ref={pageRef} onMouseOver={onMouseOver}>
        <DocxDocument
          file={fileData.mode === 'fileInfo' ? fileData.fileInfo.url : fileData.file}
          onRendered={onRendered}
        />
      </DocxWrapper>
    </ImageWrapper>
  );
});

DocxPreview.displayName = 'DocxPreview';
